import React, { useEffect } from 'react';
import { 
  CalendarIcon,
  CheckCircleIcon,
  StarIcon
} from '@heroicons/react/24/outline';

interface LeadCaptureProps {
  assessmentId?: string;
  roadmapId?: string;
  selectedAgent?: string;
  estimatedBudget?: number;
}

const LeadCapture: React.FC<LeadCaptureProps> = ({ 
  assessmentId, 
  roadmapId, 
  selectedAgent, 
  estimatedBudget 
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Initialize TidyCal when component mounts
  useEffect(() => {
    // Wait for TidyCal script to load
    const initTidyCal = () => {
      const tidycalElements = document.querySelectorAll('.tidycal-embed');
      if (window.TidyCal && tidycalElements.length > 0) {
        window.TidyCal.init();
      }
    };

    // Try to initialize immediately
    initTidyCal();
    
    // Also try after a delay in case script hasn't loaded yet
    const timer = setTimeout(initTidyCal, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="bg-gradient-to-br from-primary-50 to-blue-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center bg-primary-100 text-primary-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <StarIcon className="h-4 w-4 mr-2" />
            Accelera il Tuo Successo AI
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Prenota la Tua Call Strategica Gratuita
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            30 minuti con un AI Strategy Expert per trasformare la tua roadmap in un piano d'azione concreto
          </p>
        </div>

        {/* Value Propositions Row */}
        <div className="grid lg:grid-cols-3 gap-6 mb-12">
          {[
            {
              icon: <CalendarIcon className="h-6 w-6 text-primary-600" />,
              title: "Analisi Personalizzata",
              desc: "Review approfondita della tua roadmap con suggerimenti specifici per il tuo business"
            },
            {
              icon: <CheckCircleIcon className="h-6 w-6 text-primary-600" />,
              title: "Piano di Implementazione",
              desc: "Step concreti per iniziare subito"
            },
            {
              icon: <StarIcon className="h-6 w-6 text-primary-600" />,
              title: "Strategia Personalizzata",
              desc: "Consigli specifici per massimizzare il valore dell'AI nel tuo business"
            }
          ].map((item, index) => (
            <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                {item.icon}
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
              <p className="text-gray-600 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* TidyCal Widget - Full Width */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-6xl mx-auto">
          {/* Project summary and Social Proof Row */}
          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            {/* Project Summary */}
            {selectedAgent && (
              <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-3">Il Tuo Progetto AI:</h4>
                <div className="space-y-2">
                  <div className="text-gray-700">{selectedAgent}</div>
                  {estimatedBudget && (
                    <div className="text-2xl font-bold text-primary-600">
                      {formatCurrency(estimatedBudget)}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Social proof */}
{/*             <div className="bg-gray-50 rounded-lg p-6">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-primary-600">200+</div>
                  <div className="text-xs text-gray-600">Aziende</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-primary-600">95%</div>
                  <div className="text-xs text-gray-600">Success Rate</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-primary-600">300%</div>
                  <div className="text-xs text-gray-600">ROI Medio</div>
                </div>
              </div>
            </div> */}
          </div>

          {/* TidyCal Embed - Wider */}
          <div className="relative">
            <iframe
              src="https://tidycal.com/team/blank/prima-consulenza-agenti-ai"
              className="rounded-lg overflow-hidden shadow-inner"
              style={{ 
                width: '100%',
                height: '700px',
                border: '2px solid #e5e7eb',
                backgroundColor: '#f9fafb'
              }}
              title="Prenota una consulenza strategica"
              loading="lazy"
            />
            
            {/* Decorative elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary-100 rounded-full opacity-20 blur-2xl"></div>
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-blue-100 rounded-full opacity-20 blur-2xl"></div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 font-medium">
              ✅ Nessun impegno • 🕐 Call di 30 minuti • 💡 Consulenza gratuita
            </p>
            <p className="text-xs text-gray-400 mt-2">
              Seleziona il tuo slot preferito nel calendario sopra
            </p>
          </div>
        </div>

        {/* Additional CTA */}
        <div className="mt-16 text-center">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Non vedi orari disponibili?
            </h3>
            <p className="text-gray-600 mb-6">
              Contattaci direttamente per organizzare una call in base alla tua disponibilità
            </p>
            <div className="space-y-2">
              <a 
                href="mailto:alessandro@blankgrowth.agency" 
                className="block text-primary-600 hover:text-primary-700 font-medium"
              >
                📧 alessandro@blankgrowth.agency
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Add TidyCal types to window object
declare global {
  interface Window {
    tidycal?: {
      init: () => void;
    };
    TidyCal?: {
      init: () => void;
    };
  }
}

export default LeadCapture;